import React, { memo, useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { fetchDataDetailOptions, resetActiveLocation } from '@actions/data-detail-actions';
import { loadBatch, geocodeBatchRows } from '@actions/batch-actions';
import { getEntityType } from '@constants/config';

import Drawer from '@components/batch/batch-upload-edit/drawer';
import Content from '@components/batch/batch-upload-edit/content';
import BatchContext from '@components/batch/batch-upload-edit/batch-context';
import UploadBatchDialog from '@components/batch/batch-upload-edit/upload-batch-dialog';
import TopBar from '@components/details/top-bar';

import {
  convertCSVDataForContext,
  convertCSVErrorsForContext,
  getBatch
} from '@utils/batch-utils';

import './batch-upload-edit.scss';

const BatchUploadEdit = () => {
  const dispatch = useDispatch();
  const { id: fileId, rowId } = useParams();
  const [filter, setFilter] = useState('all');
  const batch = useMemo(() => getBatch(fileId), [fileId]);
  const {
    map_category_type: categoryTypes,
    map_category: categories,
    segment_impact_type: segmentImpactTypes
  } = useSelector(state => state.dataTypes);

  useEffect(() => {
    if (batch && batch.rows && batch.entity) {
      // Load data in the store from CSV.
      const transformedRows = batch.rows.map(row => ({
        id: row.id,
        display_row_nums: row.display_row_nums,
        data: convertCSVDataForContext(row, batch.entity, categoryTypes, categories, segmentImpactTypes),
        errors: convertCSVErrorsForContext(row, batch.errors || {})
      }));

      const entityType = getEntityType(batch.entity);
      const entityConfig = entityType?.config;

      dispatch(loadBatch({ fileId, rows: transformedRows, fileName: batch?.file?.path, entityType: batch.entity }));
      dispatch(fetchDataDetailOptions(batch.entity));
      dispatch(geocodeBatchRows(fileId, transformedRows, entityConfig));
    }
  }, [batch, categoryTypes, categories, dispatch, fileId, segmentImpactTypes]);

  useEffect(() => {
    // If rowId changes, reset the active location:
    dispatch(resetActiveLocation());
  }, [dispatch, rowId]);

  const rowIndex = useMemo(() => {
    return batch?.rows?.findIndex(item => item.id === rowId);
  }, [rowId, batch?.rows]);

  const batchContextValue = {
    batch,
    fileId,
    filter,
    rowId,
    rowIndex,
    setFilter
  };

  return (
    <BatchContext.Provider value={batchContextValue}>
      <div styleName="batch-upload-edit-container">
        <TopBar />
        <div styleName="batch-upload-edit-content">
          <Drawer />
          <Content />
        </div>
        <UploadBatchDialog />
      </div>
    </BatchContext.Provider>
  );
};

export default memo(BatchUploadEdit);
